import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  computed,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ERROR_MESSAGE, ValidationErrorMessages } from 'src/app/_helpers/utils';

const ERROR_PRIORITY: (keyof ValidationErrorMessages)[] = [
  'required',
  'min',
  'max',
  'step',
];

let uniqueIdCounter = 0;

export interface OdCellNavigateEvent {
  direction: 'up' | 'down' | 'left' | 'right';
  row: number;
  col: number;
}

/**
 * A compact number input for use inside a data table cell.
 * Reflects disabled/edited states visually and emits arrow-key
 * navigation events so the parent can move focus between cells.
 */
@Component({
  selector: 'app-table-input',
  templateUrl: './table-input.component.html',
  styleUrl: './table-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
})
export class TableInputComponent {
  /** The control this cell edits (the parent already has it in hand). */
  control = input.required<FormControl>();

  /** Accessible label for the input */
  label = input.required<string>();

  /** Step value for number inputs (default: 0.001) */
  step = input(0.001);

  /** Minimum allowed value for number inputs (optional) */
  min = input<number | undefined>(undefined);

  /** Maximum allowed value for number inputs (optional) */
  max = input<number | undefined>(undefined);

  /** Row index within the table (0-based), used for navigation */
  rowIndex = input(0);

  /** Column index within the table (0-based), used for navigation */
  colIndex = input(0);

  /** When true, the cell is visible but cannot be edited (e.g. read-only role, finalised) */
  readOnly = input(false);

  /**
   * Emits when an arrow key should move focus to an adjacent cell.
   * Left and right only emit when the cursor is already at the start
   * or end of the input value respectively.
   */
  navigate = output<OdCellNavigateEvent>();

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly inputEl = viewChild<ElementRef<HTMLInputElement>>('inputEl');

  private readonly instanceId = `table-input-${uniqueIdCounter++}`;

  /** Human-readable text for this cell's first active validation error, if any */
  readonly errorMessage = computed<string | null>(() => {
    const errors = this.control().errors;
    if (!errors) return null;
    const key = ERROR_PRIORITY.find((k) => errors[k]);
    if (!key) return null;
    return ERROR_MESSAGE[key](errors[key] as never);
  });

  readonly errorId = `${this.instanceId}-error`;
  readonly inputId = this.instanceId;

  /** Public: called by the parent via viewChildren to move keyboard focus to this cell */
  focus(): void {
    const el = this.inputEl()?.nativeElement;
    el?.focus();
    el?.select();
  }

  /**
   * Called by the parent after externally mutating this cell's control
   * (e.g. copyColumnVoltages marking it dirty, or mapVoltageReadingsToFormControls
   * enabling/disabling it), so OnPush re-evaluates [class.is-edited]/[class.has-error].
   */
  markEdited(): void {
    this.cdr.markForCheck();
  }

  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLInputElement;
    const r = this.rowIndex();
    const c = this.colIndex();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.navigate.emit({ direction: 'down', row: r, col: c });
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigate.emit({ direction: 'up', row: r, col: c });
        break;
      case 'ArrowRight':
        if (target.selectionStart === target.value.length) {
          event.preventDefault();
          this.navigate.emit({ direction: 'right', row: r, col: c });
        }
        break;
      case 'ArrowLeft':
        if (target.selectionStart === 0) {
          event.preventDefault();
          this.navigate.emit({ direction: 'left', row: r, col: c });
        }
        break;
    }
  }
}
